import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { MedicalDirectorSidebar } from '@/integrations/medical-director/medicalDirectorSidebar';
import { sessionListCanFitSplitAtom } from '@heidi/session';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';

/**
 * Which surface hosts the in-session Ask Heidi chat while
 * `evidenceSidebarOpenAtom` is true.
 *
 * - `panel`: the right-hand resizable `EvidenceSidebarPanel`.
 * - `drawer`: the full-height `EvidenceSidebarDrawer`.
 * - `none`: nothing can paint it — in-session Evidence is off (the floating
 *   bar keeps its legacy input) or the app runs inside the Medical Director
 *   widget, whose legacy modal owns Ask Heidi.
 *
 * INVARIANT — `panel` is a promise about the layout, not a measurement: every
 * layout that renders `SessionAskHeidiContainer` (which yields the floating
 * bar on `panel` and `drawer` alike) must mount an `EvidenceSidebarPanel`
 * wherever this resolves `panel`. Today that is `DualPanelLayout` on
 * `/scribe/session/[id]` and `SessionsSplitWorkspace` on `/scribe/sessions`
 * while the split fits — the `!canFitSplit` band is exactly why that route
 * resolves `drawer`. A mounted panel that cannot get width hands back to the
 * bar itself (`useEvidenceSidebarPanel`); an unmounted one cannot, and
 * `useAskHeidiSurfaceReconciler` only recovers `none`. A new session layout
 * that mounts the bar must mount a panel host or the drawer, or Ask Heidi
 * goes dark there on send.
 */
export type AskHeidiSurface = 'panel' | 'drawer' | 'none';

export interface AskHeidiSurfaceInputs {
  /** `useEvidenceAccess().isInSessionEvidenceShown`. */
  isInSessionEvidenceShown: boolean;
  /** Viewport below the Tailwind `md` breakpoint (`useBreakpoint().isLessThan('md')`). */
  isBelowMd: boolean;
  /**
   * On `/scribe/sessions[/id]` the panel only mounts inside the split
   * workspace, so a single-pane layout must fall back to the drawer (EVI-2119).
   */
  isSessionsRoute: boolean;
  /** `sessionListCanFitSplitAtom`: the sessions workspace can hold list + detail. */
  canFitSplit: boolean;
  isMedicalDirector: boolean;
}

export interface AskHeidiSurfaceState {
  surface: AskHeidiSurface;
  /**
   * In-session Evidence is granted. Only then is a `none` surface a real gap:
   * without access the floating bar renders its legacy input regardless of
   * the open signal, and access may still be resolving.
   */
  hasEvidenceAccess: boolean;
}

/** Route prefix for the drawer election: the plural split route and its children only. */
export const isSessionsSplitRoute = (pathname: string): boolean =>
  pathname.startsWith('/scribe/sessions');

/**
 * One decision for the three Ask Heidi renderers. The floating bar
 * (`SessionAskHeidiContainer`), the side panel (`useEvidenceSidebarPanel`) and
 * the drawer (`EvidenceSidebarDrawer`) each read this instead of re-deriving the
 * breakpoint and split predicates, so the bar can never yield to a surface
 * that will not paint.
 */
export function resolveAskHeidiSurface({
  isInSessionEvidenceShown,
  isBelowMd,
  isSessionsRoute,
  canFitSplit,
  isMedicalDirector,
}: AskHeidiSurfaceInputs): AskHeidiSurface {
  if (!isInSessionEvidenceShown) return 'none';
  const wantsDrawer = isBelowMd || (isSessionsRoute && !canFitSplit);
  if (wantsDrawer) return isMedicalDirector ? 'none' : 'drawer';
  return 'panel';
}

export function useAskHeidiSurface(): AskHeidiSurfaceState {
  const { isLessThan } = useBreakpoint();
  const canFitSplit = useAtomValue(sessionListCanFitSplitAtom);
  const { pathname } = useRouter();
  const { isInSessionEvidenceShown } = useEvidenceAccess();

  const surface = resolveAskHeidiSurface({
    isInSessionEvidenceShown,
    isBelowMd: isLessThan('md'),
    isSessionsRoute: isSessionsSplitRoute(pathname),
    canFitSplit,
    isMedicalDirector: MedicalDirectorSidebar.isMedicalDirector(),
  });

  return { surface, hasEvidenceAccess: isInSessionEvidenceShown };
}
